import React, { useEffect, useState } from 'react'
import ButtonComponent from '../shared/button/button.component';
import { OrgAccountBilling, SeatUsage } from '../../models/organization.model';
import { UtilityService } from '../../services/UtilityService';
import { buttonLabels, pageTitles, tenureTexts, ToasterTexts } from '../shared/constants';
import { CancelIcon, HistoryCheckIcon, InfoCircleIcon, MasterCardIcon, PartialStatusIcon } from '../../icon';
import { AppRoutes } from '../routes/route.path';
import { UserService } from '../../services/UserService';
import * as selector from "../../selectors/selectors";
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { setHeaderTitle } from '../../helpers/header.helper';
import { Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import chargebeeService from '../../services/chargebee.service';
import setToast from '../../helpers/toaster.helper';
import { ToasterType } from '../shared/toaster/toaster.model';


interface IProps {
    AccountBillingData: OrgAccountBilling;
    PaymentMethodDetails: any;
    InvoiceOnClick: (number) => void
}

const Subscription = (props: IProps) => {
    const navigate = useNavigate();
    const IsDarkMode: boolean = useSelector(selector.isDarkMode);

    const [showSubscriptionDialog, setShowSubscriptionDialog] = useState<boolean>(false)
    const [isLoading, setIsloading] = useState<boolean>(false)
    const [selectedIds, setSelectedIds] = useState<string[]>([])

    const getTotalCost = () => {
        const planDetails = props.AccountBillingData?.CurrentPlanDetails
        if (!!planDetails?.length) {
            let cost = 0;
            if (planDetails[0].IsYearPlan) {
                planDetails.map((x) => {
                    cost = cost + (x.Quantity * x.YPrice)
                })
            }
            else {
                planDetails.map((x) => {
                    cost = cost + (x.Quantity * x.Price)
                })
            }
            return `$${cost}`;
        }
        return ""
    }

    const getEachPlanTotalCost = (seat: SeatUsage) => {
        const data = props.AccountBillingData?.CurrentPlanDetails.find(x => x.PlanName === seat.SeatType)
        if (!!data) {
            return data.IsYearPlan ? data.YPrice * data.Quantity : data.Price * data.Quantity;
        }
        return "";
    }

    const addToDateAndFormat = () => {
        const date = new Date(UtilityService.convertDateToDay(props.AccountBillingData?.CurrentPlanDetails[0].EndDate));
        let plans = props.AccountBillingData?.CurrentPlanDetails
        if (!!plans?.length) {
            if (!plans[0].IsYearPlan) {
                date.setMonth(date.getMonth() + 1);
            } else {
                date.setFullYear(date.getFullYear() + 1);
            }
        }
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
        });
    }

    const goToRecharge = (IsAddonRecharge?: boolean) => {
        navigate(AppRoutes.NEW_RECHARGE, { state: { OrganizationId: UserService.getCurrentUserFromLocalStorage()?.OrgId, IsAddonRecharge: IsAddonRecharge } })
    }

    const cancelSubscription = () => {
        setIsloading(true)
        chargebeeService.cancelSubscription(selectedIds).then((x) => {
            setShowSubscriptionDialog(false)
            setSelectedIds([])
            setIsloading(false)
            setToast(ToasterTexts.CANCEL_SUBSCRIPTION_SUCCESS, ToasterType.SUCCESS);
        }).catch((err) => {
            setShowSubscriptionDialog(false)
            setSelectedIds([])
            setIsloading(false)
            const selectedPlans = (props.AccountBillingData?.CurrentPlanDetails || [])
                .filter(p => selectedIds.includes(p.SubscriptionId));
            const errors = err?.response?.data?.errors;
            const message = Array.isArray(errors) && errors.length
                ? errors.map((error) => {
                    const plan = selectedPlans.find(
                        p => p.SubscriptionId === error.subscription_id
                    );
                    return `${plan?.PlanName ?? error.subscription_id}: ${error.message}`;
                }).join(", ")
                : ToasterTexts.CANCEL_SUBSCRIPTION_FAILED;

            setToast(message || ToasterTexts.CANCEL_SUBSCRIPTION_FAILED, ToasterType.DANGER);
        })
    }

    const closeSubscriptionDialog = () => {
        setShowSubscriptionDialog(false);
        setSelectedIds([])
    }

    const handleCheckboxChange = (plan) => {
        setSelectedIds((prev) => {
            const isAlreadySelected = prev.some(x => x === plan?.SubscriptionId)

            if (isAlreadySelected) {
                return prev.filter(x => x !== plan?.SubscriptionId)
            }

            return [...prev, plan.SubscriptionId]
        })
    }

    useEffect(() => {
        setHeaderTitle(pageTitles.ACCOUNT_BILLING)
        return setHeaderTitle
    }, [])

    return (
        <div className='w-p-96 mx-auto d-flex flex-direction pt-3 h-p-98 o-y-scroll'>
            <div className={`w-p-96 d-flex flex-direction justify-between mx-auto br-2 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"}`}>
                <div className='d-flex justify-between align-center w-p-96 mx-auto py-2'>
                    <span className='h2-medium color-primary-accent'>Current Plan Summary</span>
                    <div className='d-flex'>
                        <ButtonComponent ButtonLabel={'+ Add Seats'} Variant={'secondary-btn-small'} OnClick={() => goToRecharge(false)} />
                        <ButtonComponent ButtonLabel={'+ Add Credits'} Variant={'secondary-btn-small'} ClassName='ml-2' OnClick={() => goToRecharge(true)} />
                        <ButtonComponent ButtonLabel={buttonLabels.CANCEL_SUBSCRIPTION} Variant={'secondary-btn-small'} ClassName='ml-2' OnClick={() => setShowSubscriptionDialog(true)} />
                    </div>
                </div>
                <div className='bb-primary-divider-4'></div>

                <div className='w-p-96 mx-auto d-flex align-center py-2 pb-2'>
                    <span className='h3-medium color-account-billing-text-gray bb-primary-divider-top w-p-15'>Plan Details</span>
                    <div className='d-flex w-p-80'>
                        <div className='ml-10 d-flex flex-direction w-p-30'>
                            <span className='h4-regular color-account-billing-text-gray'> Seats</span>
                            <span className='h3-medium color-primary-accent'>
                                {!!props.AccountBillingData?.SeatUsage?.length && props.AccountBillingData?.SeatUsage.map(x => `${x.Users.Total} ${x.SeatType}`).join(" + ")}
                            </span>
                        </div>
                        <div className='ml-10 d-flex flex-direction w-p-30'>
                            <span className='h4-regular color-account-billing-text-gray'> Credits Pool</span>
                            <span className='h3-medium color-primary-accent'>
                                {!!props.AccountBillingData?.SeatUsage?.length && props.AccountBillingData.GroupMsgCredits.Total}
                            </span>
                        </div>
                        <div className='ml-10 d-flex flex-direction w-p-30'>
                            <span className='h4-regular color-account-billing-text-gray'> Plan Cost</span>
                            <span className='h3-medium color-primary-accent'>
                                {!!props.AccountBillingData?.SeatUsage?.length && getTotalCost()}
                            </span>
                        </div>

                    </div>
                </div>
                <div className='w-p-96 mx-auto d-flex align-center py-2'>
                    <span className='h3-medium color-account-billing-text-gray bb-primary-divider-top w-p-15'>Billing Details</span>
                    <div className='d-flex w-p-80'>
                        <div className='ml-10 d-flex flex-direction w-p-30'>
                            <span className='h4-regular color-account-billing-text-gray'>Last Billing Date</span>
                            <span className='h3-medium color-primary-accent'>
                                {!!props.AccountBillingData?.SeatUsage?.length && UtilityService.convertDateToDay(props.AccountBillingData?.CurrentPlanDetails[0].StartDate)}
                            </span>
                        </div>
                        <div className='ml-10 d-flex flex-direction w-p-30'>
                            <span className='h4-regular color-account-billing-text-gray'> Billing Cycle</span>
                            <span className='h3-medium color-primary-accent'>
                                {!!props.AccountBillingData?.CurrentPlanDetails[0] ? props.AccountBillingData?.CurrentPlanDetails[0]?.IsYearPlan ? tenureTexts.ANNUAL : tenureTexts.MONTH : ""}
                            </span>
                        </div>
                        <div className='ml-10 d-flex flex-direction w-p-30'>
                            <span className='h4-regular color-account-billing-text-gray'> Auto Renewal</span>
                            <span className='h3-medium color-primary-accent'>
                                {!!props.AccountBillingData?.SeatUsage?.length && props.AccountBillingData?.CurrentPlanDetails?.find(x => x.RenewDate !== null)?.RenewDate && UtilityService.convertDateToDay(props.AccountBillingData?.CurrentPlanDetails?.find(x => x.RenewDate !== null).RenewDate)}
                            </span>
                        </div>

                    </div>
                </div>
                <div className='d-flex w-p-96 mx-auto py-2'>
                    <div className='d-flex flex-direction w-p-35'>
                        <span className='color-account-billing-text-gray h5-regular pb-1'>Payment Method</span>
                        <div className='d-flex align-center color-primary-accent h4-regular msg-composer-secure-file-border py-2 br-2'>
                            <MasterCardIcon className='ml-2' />
                            <span className='mx-2'>Mastercard</span>
                            <span className='color-primary-accent h5-medium ml-2'>{!!props.PaymentMethodDetails?.masked_number ? `**** **** **** ${props.PaymentMethodDetails?.masked_number?.slice(12)}` : "--"}</span>
                        </div>
                    </div>
                    <div className='w-p-5'></div>
                    <div className='d-flex flex-direction w-p-60'>
                        <span className='color-account-billing-text-gray h5-regular pb-1'>Active Users</span>
                        <div className='d-flex align-center color-primary-accent h4-regular msg-composer-secure-file-border py-2 br-2 w-p-100'>
                            <div className='w-p-96 mx-auto d-flex align-center flex-wrap'>
                                {props.AccountBillingData?.CurrentPlanDetails?.length && props.AccountBillingData.SeatUsage.map((x) => (!x.SeatType?.toLowerCase().includes("10dlc") &&
                                    <div className='w-p-33 d-flex align-center'>
                                        <span className='h4-regular color-account-billing-text-gray mr-2 w-p-55' style={{ wordBreak: "break-all" }}>{x.SeatType}</span>
                                        <span className='color-primary-accent h2-medium d-flex align-center w-12'>{`${x.Users.Used} / ${x.Users.Total}`}{x.Users.Used === x.Users.Total ? <HistoryCheckIcon className='ml-1' /> : <PartialStatusIcon className='ml-1' />} </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`w-p-96 mx-auto br-2 bg-color-secondary mt-2 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"}`}>
                <div className='w-p-96 mx-auto d-flex justify-between py-2'>
                    <div >
                        <span className='color-primary-accent h2-medium'>Current Billing Terms | </span>
                        {props.AccountBillingData?.CurrentPlanDetails?.length && <span className='color-primary-accent h2-medium'>{UtilityService.convertDateToDay(props.AccountBillingData?.CurrentPlanDetails[0].StartDate)} - </span>}
                        {props.AccountBillingData?.CurrentPlanDetails?.length && <span className='color-primary-accent h2-medium'>{UtilityService.convertDateToDay(props.AccountBillingData?.CurrentPlanDetails[0].EndDate)}</span>}
                    </div>
                    <ButtonComponent ButtonLabel={'Invoice History'} Variant={'secondary-btn-small'} OnClick={() => { props.InvoiceOnClick(2) }} />
                </div>
                <div className='bb-primary-divider-4'></div>

                <div className='w-p-96 mx-auto py-2 d-flex justify-between'>
                    <div className='d-flex flex-direction w-p-50'>
                        {!!props.AccountBillingData?.SeatUsage?.length && props.AccountBillingData?.SeatUsage.map(x =>
                            <div className='d-flex justify-between'>
                                <span className='h3-regular color-account-billing-text-gray'>{x.Users.Total} {x.SeatType} Seats Subscription <InfoCircleIcon className='ml-1' /></span>
                                <span className='color-primary-accent h3-regular'>${getEachPlanTotalCost(x)}</span>
                            </div>
                        )}

                        <div className='bb-primary-divider-4 mt-2'></div>
                        <div className='d-flex justify-between mt-2'>
                            <span className='h3-medium color-primary-accent'>Total Bill <InfoCircleIcon className='ml-1' /></span>
                            <span className='color-primary-accent h3-medium'>{getTotalCost()}</span>
                        </div>
                    </div>
                    <div className='d-flex flex-direction w-p-45'>
                        <span className='color-account-billing-text-gray h5-regular pb-1'>Payment Method</span>
                        <div className='d-flex align-center color-primary-accent h4-regular msg-composer-secure-file-border py-2 br-2'>
                            <MasterCardIcon className='ml-2' />
                            <span className='mx-2'>Mastercard</span>
                            <span className='color-primary-accent h5-medium ml-2'>{!!props.PaymentMethodDetails?.masked_number ? `**** **** **** ${props.PaymentMethodDetails?.masked_number?.slice(12)}` : "--"}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className={`w-p-96 mx-auto br-2 bg-color-secondary my-2 ${IsDarkMode ? "bg-color-sidebar-bg" : "bg-color-secondary"}`}>
                <div className='w-p-96 mx-auto d-flex justify-between py-2'>
                    <div >
                        <span className='color-primary-accent h2-medium'>Next Billing Terms | </span>
                        {props.AccountBillingData?.CurrentPlanDetails?.length && <span className='color-primary-accent h2-medium'>{UtilityService.convertDateToDay(props.AccountBillingData?.CurrentPlanDetails[0].EndDate)} - </span>}
                        {props.AccountBillingData?.CurrentPlanDetails?.length && <span className='color-primary-accent h2-medium'>{addToDateAndFormat()}</span>}
                    </div>
                    <ButtonComponent ButtonLabel={'Modify Plan'} Variant={'secondary-btn-small'} OnClick={() => goToRecharge(false)} />
                </div>
                <div className='bb-primary-divider-4'></div>

                <div className='w-p-96 mx-auto py-2 d-flex justify-between'>
                    <div className='d-flex flex-direction w-p-50'>
                        {!!props.AccountBillingData?.SeatUsage?.length && props.AccountBillingData?.SeatUsage.map(x =>
                            <div className='d-flex justify-between'>
                                <span className='h3-regular color-account-billing-text-gray'>{x.Users.Total} {x.SeatType} Seats Subscription <InfoCircleIcon className='ml-1' /></span>
                                <span className='color-primary-accent h3-regular'>${getEachPlanTotalCost(x)}</span>
                            </div>
                        )}

                        <div className='bb-primary-divider-4 mt-2'></div>
                        <div className='d-flex justify-between mt-2'>
                            <span className='h3-medium color-primary-accent'>Total Bill <InfoCircleIcon className='ml-1' /></span>
                            <span className='color-primary-accent h3-medium'>{getTotalCost()}</span>
                        </div>
                    </div>
                    <div className='d-flex flex-direction w-p-45'>
                        <span className='color-account-billing-text-gray h5-regular pb-1'>Payment Method</span>
                        <div className='d-flex align-center color-primary-accent h4-regular msg-composer-secure-file-border py-2 br-2'>
                            <MasterCardIcon className='ml-2' />
                            <span className='mx-2'>Mastercard</span>
                            <span className='color-primary-accent h5-medium ml-2'>{!!props.PaymentMethodDetails?.masked_number ? `**** **** **** ${props.PaymentMethodDetails?.masked_number?.slice(12)}` : "--"}</span>
                        </div>
                    </div>
                </div>
            </div>
            {
                showSubscriptionDialog &&
                <Dialog maxWidth="sm" fullWidth={true} open={showSubscriptionDialog} onClose={() => { closeSubscriptionDialog() }} aria-labelledby="customized-dialog-title" aria-describedby="alert-dialog-description"
                    BackdropProps={{ style: { backgroundColor: "transparent" } }}
                    classes={{ paper: `w-66 br-2 ${IsDarkMode ? "bg-color-sidebar-bg" : "dialogue-shadow"}` }}
                    disableEscapeKeyDown
                >
                    <DialogTitle className="dialog-h1">
                        <div className={` d-flex`} id="customized-dialog-title">
                            {"Cancel Subscription"}
                            <Tooltip title='Close' placement='bottom'>
                                <span className="ml-auto">
                                    <CancelIcon className={"c-pointer"} onClick={closeSubscriptionDialog} />
                                </span>
                            </Tooltip>
                        </div>
                    </DialogTitle>
                    <DialogContent className="px-2 py-0">
                        <div className="mt-1">
                            <div className='h3-regular ml-2'>Select Plans to Cancel Subscription</div>
                            {props.AccountBillingData?.CurrentPlanDetails?.map(x =>
                                <div className='d-flex p-2 align-center'>
                                    <Checkbox
                                        checked={selectedIds?.some(p => p === x?.SubscriptionId)}
                                        onChange={() => { handleCheckboxChange(x) }}
                                        classes={{ checked: "selected-checkbox", indeterminate: "selected-checkbox" }}
                                        className='p-0' />
                                    <div className='ml-2 h4-regular primary-light'>{x.PlanName}</div>
                                </div>
                            )
                            }
                        </div>
                    </DialogContent>
                    <DialogActions className='mt-1 pb-2 pr-2'>
                        <ButtonComponent Variant="secondary-btn-small" OnClick={closeSubscriptionDialog}
                            ClassName={`d-flex  mr-1 ml-auto`}
                            ButtonLabel={buttonLabels.CANCEL} />
                        <ButtonComponent Variant={!selectedIds?.length ? "disabled-btn-small" : "primary-btn-small"} Loading={isLoading} ClassName={`d-flex  px-4`} ButtonLabel={"Cancel Subscription"} OnClick={cancelSubscription} />
                    </DialogActions>
                </Dialog>
            }
        </div>
    )
}

export default Subscription;